// Formatage partagé des fourchettes de la grille tarifaire (montants et
// jours), utilisé à la fois par l'affichage complet (pricing-grid.tsx), les
// badges factuels du hero (hero-cta.tsx) et le teaser Codeur.com
// (DEFAULT_MESSAGES_PROMPT via generateMessagesCore).

import type { OfferLanguage, PricingGrid } from "@/db/schema/content-items";

const LOCALE: Record<OfferLanguage, string> = { fr: "fr-FR", en: "en-GB" };

export function formatEur(cents: number, language: OfferLanguage = "fr"): string {
  return new Intl.NumberFormat(LOCALE[language], {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

// Fourchette budgétaire : basse et haute affichées ensemble, sauf si elles
// sont égales (montant exceptionnellement ferme) où un seul chiffre suffit.
export function formatEurRange(low: number, high: number, language: OfferLanguage = "fr"): string {
  if (high <= low) return formatEur(low, language);
  return `${formatEur(low, language)} – ${formatEur(high, language)}`;
}

export function formatDays(days: number, language: OfferLanguage = "fr"): string {
  const n = new Intl.NumberFormat(LOCALE[language], { maximumFractionDigits: 1 }).format(days);
  if (language === "en") return `${n} ${days > 1 ? "days" : "day"}`;
  return `${n} ${days > 1 ? "jours" : "jour"}`;
}

// Même principe que formatEurRange : une seule valeur si basse et haute
// coïncident.
export function formatDaysRange(low: number, high: number, language: OfferLanguage = "fr"): string {
  if (!low && !high) return "";
  if (high <= low) return formatDays(low, language);
  return `${formatDays(low, language)} – ${formatDays(high, language)}`;
}

// Fourchette prix/délai du projet, calculée sur les lignes "base" uniquement
// (jamais les compléments optionnels) : source unique réutilisée par le hero,
// le teaser et le pipeline "Hermès", pour qu'ils ne puissent jamais raconter
// un chiffre différent de celui de la grille tarifaire réelle. `null` quand la
// grille n'existe pas encore ou n'a aucune ligne "base".
export function computePricingRange(
  grid: PricingGrid | null | undefined,
  language: OfferLanguage = "fr"
): { priceRange: string | null; delayRange: string | null } {
  const baseLines = grid?.lines.filter((l) => l.kind === "base") ?? [];
  if (baseLines.length === 0) return { priceRange: null, delayRange: null };

  const priceRange = formatEurRange(
    baseLines.reduce((sum, l) => sum + l.amountLow, 0),
    baseLines.reduce((sum, l) => sum + l.amountHigh, 0),
    language
  );
  const delayRange = formatDaysRange(
    baseLines.reduce((sum, l) => sum + l.daysLow, 0),
    baseLines.reduce((sum, l) => sum + l.daysHigh, 0),
    language
  );
  return { priceRange, delayRange };
}
